/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

/* eslint-disable object-curly-newline */

import React from 'react';
import {
    object, bool, string, func, shape, arrayOf,
} from 'prop-types';
import { connect } from 'react-redux';
import * as Joi from 'joi-browser';
import { withStyles } from '@material-ui/core/styles';

import Form from '../Form';
import { form as FormStyle } from '../../style/form';
import EventDispatcher from '../../state/reducers/EventDispatcher';
import CreditCardMask from './InputMask/CreditCardMask';
import ExpirationDateMask from './InputMask/ExpirationDateMask';
import { getCreditCardType } from '../../utils/formater';
import configENV from '../../config';
import VIsmallcard from '../../assets/svg/VIsmallcard';
import MCsmallcard from '../../assets/svg/MCsmallcard';
import AXsmallcard from '../../assets/svg/AXsmallcard';
import DSsmallcard from '../../assets/svg/DSsmallcard';
import PYsmallcard from '../../assets/svg/PYsmallcard';
import APsmallcard from '../../assets/svg/APsmallcard';
import GPsmallcard from '../../assets/svg/GPsmallcard';
import VMsmallcard from '../../assets/svg/VMsmallcard';
import { isEmpty, deepObjectExtend } from '../../utils/object';

const { CC_TOKENIZATION_SOURCE_ID, PERSIST_KEY } = configENV;

const styles = ({ typography, palette }) => deepObjectExtend(FormStyle(typography, palette), {
    cardTypeIcon: {
        position: 'relative',
        // Used contains Mui class name to avoid CSS issue on Account MFE
        '& [class*="MuiListItemText-root"]': {
            display: 'inline-flex',
            margin: 0,
        },
        // Used contains Mui class name to avoid CSS issue on Account MFE
        '& [class*="MuiListItemIcon-root"]': {
            position: 'absolute',
            right: '0px',
            top: '7px',
        },
    },
    menuIcon: {
        // Used contains Mui class name to avoid CSS issue on Account MFE
        '& [class*="MuiListItemIcon-root"]': {
            minWidth: '30px',
        },
    },
});

const cardIcons = {
    VI: VIsmallcard, // Visa card
    MC: MCsmallcard, // Master card
    AX: AXsmallcard, // Amex card
    DS: DSsmallcard, // Discover card
    PY: PYsmallcard, // PayPal
    AP: APsmallcard, // Apple Pay
    GP: GPsmallcard, // Google Pay
    VM: VMsmallcard, // Venmo
};
class CreditCardForm extends Form {
    state = {
        data: {
            cardtype: '',
            cardnumber: '',
            expiration: '',
            cardholder: '',
            validation: false,
            defaultcard: false,
        },
        walletId: '',
        hideDefaultCheckbox: false,
        adornment: {
            cardnumber: '',
        },
        replaceMask: {
            cardnumber: '[\\D]',
        },
        replaceMaskWith: {
            cardnumber: '',
        },
        errors: {},
    };

    schema = {
        cardtype: Joi.string()
            .required()
            .label('Card Type'),
        cardnumber: Joi.string()
            .alphanum()
            .regex(/^(3[47]\d{13}|[456]\d{15})$/i, 'card number')
            .min(15)
            .required()
            .label('Card Number'),
        expiration: Joi.string()
            .regex(/^[0-9]{2}\/[0-9]{2}$/i, 'MM/YY')
            .required()
            .label('Expiration Date'),
        cardholder: Joi.string()
            .trim()
            .max(50)
            .required()
            .label('Name on Card'),
        defaultcard: Joi.boolean().optional(),
        validation: Joi.boolean()
            .valid(true)
            .required(),
    };

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps = (props) => {
        const { errorField } = props;
        if (!isEmpty(errorField) && errorField.name) {
            const errors = {
                [errorField.name]: errorField.message,
            };
            this.setState({
                errors,
            });
        }
    };

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount = () => {
        const {
            modal: { content: walletObj },
        } = this.props;
        if (walletObj) {
            const {
                walletId,
                cardHolderName,
                cardType,
                expiryDate,
                lastFourDigits,
                subsIndicator,
            } = walletObj;
            // AMEX = 15 digits
            let cardnumber = lastFourDigits ? `\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022${lastFourDigits}` : '';
            // VISA, MASTER, DISCOVER have 16 digits
            if (cardType !== 'AX') {
                cardnumber = lastFourDigits ? `\u2022${cardnumber}` : '';
            }
            const data = {
                cardtype: cardType || '',
                cardnumber,
                expiration: expiryDate || '',
                cardholder: cardHolderName || '',
                defaultcard: subsIndicator === '1',
                validation: true,
            };
            const hideDefaultCheckbox = subsIndicator === '1';
            const IconType = cardIcons[cardType];
            const adornment = IconType ? { cardnumber: <IconType /> } : '';
            const errors = {};
            if (cardnumber) {
                errors.cardnumber = 'Re-enter Card Number';
            }
            const validateExpiratoinDate = this.validateExpiratoinDate(expiryDate);
            if (validateExpiratoinDate.errorMessage) {
                errors.expiration = validateExpiratoinDate.errorMessage;
                data.validation = validateExpiratoinDate.isValid;
            }
            this.setState({
                data,
                walletId,
                hideDefaultCheckbox,
                adornment,
                errors,
            });
        }
    };

    handleCreditCardChange = (updatedData) => {
        const { data } = this.state;
        const { cardnumber } = updatedData;
        const cardType = getCreditCardType(cardnumber);
        const IconType = cardIcons[cardType];
        const adornment = IconType ? { cardnumber: <IconType /> } : '';
        data.cardtype = cardType;
        const { errors } = this.state;
        if (
            (adornment === '' && cardnumber.length > 3)
            || cardnumber.match(/^[012789]\d?/)
            || cardnumber.match(/^(35|36|38|39)\d?/)
        ) {
            errors.cardnumber = 'Enter Visa, Mastercard, Amex, or Discover';
        }
        this.setState({ adornment, data, errors });
    };

    validateExpiratoinDate = (expiration) => {
        let errorMessage = '';
        let isValid = true;
        if (expiration) {
            const expiredDate = expiration.replace(/\D/g, '');
            const month = expiredDate.substr(0, 2);
            const year = expiredDate.substr(2, 2);
            const now = new Date();
            const nowMonth = `0${(now.getMonth() + 1)}`.slice(-2);
            const nowYear = Number(now
                .getFullYear()
                .toString()
                .substr(2, 2));

            let errorCounter = 0;
            if (month.length === 2 && (Number(month) === 0 || Number(month) > 12)) {
                errorCounter += 1;
                errorMessage = 'Invalid month';
            }
            if (
                year.length === 2
                && (Number(year) < nowYear || Number(year) > nowYear + 15)
            ) {
                errorCounter += 1;
                if (errorCounter === 1) {
                    errorMessage = 'Invalid year';
                } else {
                    errorMessage = 'Invalid date';
                }
            }
            if (expiredDate.length === 4) {
                const expDate = new Date(['20', year, '-', month, '-01'].join(''));
                if (expDate <= now) {
                    if ((month === nowMonth) && (Number(year) === nowYear)) {
                        errorCounter += 1;
                        errorMessage = 'Cannot expire in current month';
                    } else {
                        errorCounter += 1;
                        errorMessage = 'Card is expired';
                    }
                }
            }
            if (errorCounter > 0) isValid = false;
            else isValid = true;
        }

        return {
            errorMessage,
            isValid,
        };
    };

    handleExpirationDateChange = (updatedData) => {
        const { errors, data } = this.state;
        const { expiration } = updatedData;
        const validateExpirationDate = this.validateExpiratoinDate(expiration);
        if (validateExpirationDate.errorMessage) {
            errors.expiration = validateExpirationDate.errorMessage;
        } else {
            delete errors.expiration;
        }
        data.validation = validateExpirationDate.isValid;
        this.setState({ data, errors });
    };

    doSubmit = (submitData) => {
        const { data, walletId } = this.state;
        const subsIndicator = data.defaultcard ? '1' : '0';
        const wallet = {
            walletId,
            cardType: data.cardtype,
            payType: 'CC',
            cardHolderName: data.cardholder,
            cardNumber: data.cardnumber,
            firstTwoDigits: data.cardnumber.substr(0, 2),
            lastFourDigits: data.cardnumber.substr(data.cardnumber.length - 4),
            expiryDate: data.expiration,
            // subsIndicator: currentWallet.length === 0 ? '0' : subsIndicator, // Do not set the 1st credit card as the default card
            subsIndicator,
            tokenFlag: '0',
            user: 'PWA-DESKTOP',
            sourceID: CC_TOKENIZATION_SOURCE_ID,
        };
        submitData.handleSaveCreditCard(wallet);
    };

    mobileTemplate = (props, state) => {
        /* eslint-disable max-len */
        const {
            isReadyState,
            classes,
            handleSaveCreditCard,
        } = props;
        const {
            data: { defaultcard, cardtype },
            hideDefaultCheckbox,
        } = state;
        let {
            modal: { title: modalTitle },
        } = props;
        let submitButtonLabel = '';
        if (modalTitle) {
            modalTitle = modalTitle.replace(/^(Edit|Add)/, '').trim();
        }
        if (modalTitle.indexOf('Add') !== -1) {
            submitButtonLabel = isReadyState
                ? ''.concat('Add ', modalTitle)
                : 'Adding...';
        } else {
            submitButtonLabel = isReadyState
                ? ''.concat('Save ', modalTitle)
                : 'Saving...';
        }

        const submitData = {
            handleSaveCreditCard,
        };

        const cardTypesWithIcon = [
            {
                id: 'VI',
                name: 'Visa',
                icon: cardIcons.VI,
            },
            {
                id: 'MC',
                name: 'Master Card',
                icon: cardIcons.MC,
            },
            {
                id: 'AX',
                name: 'American Express',
                icon: cardIcons.AX,
            },
            {
                id: 'DS',
                name: 'Discover',
                icon: cardIcons.DS,
            },
        ];

        return (
            <>
                <p>We Accept: Visa, Mastercard, American Express and Discover</p>
                <form
                    onSubmit={(event) => this.handleSubmit(event, submitData)}
                    className={classes.form}
                >
                    {this.renderInput({
                        name: 'cardholder',
                        dataTest: { field: 'my-account-cc-card-name' },
                        label: 'Name on Card',
                        type: 'text',
                        pattern: '^[a-zA-Z\\s\\.\\-&]*$',
                        length: 50,
                    })}
                    {this.renderSelect({
                        name: 'cardtype',
                        label: 'Payment Type *',
                        options: cardTypesWithIcon,
                        cardTypeIcon: true,
                        classes,
                    })}
                    {this.renderInput({
                        name: 'cardnumber',
                        label: 'Account Number',
                        dataTest: { field: 'my-account-cc-card-number' },
                        type: 'tel',
                        pattern: '^[\\d]*$',
                        length: null,
                        // adornment: iconType, // commenting out this to reuse functionality(show icon according to the account number) in future if required
                        // adornmentPosition: 'end',
                        mask: CreditCardMask,
                        onChangeHandler: this.handleCreditCardChange,
                        customClass: `-cardtype-${cardtype}-`,
                    })}
                    {this.renderInput({
                        name: 'expiration',
                        label: 'Expiration Date (MM/YY)',
                        dataTest: { field: 'my-account-cc-exp-date' },
                        type: 'tel',
                        pattern: '^[\\d\\s\\/]*$',
                        length: null,
                        mask: ExpirationDateMask,
                        onChangeHandler: this.handleExpirationDateChange,
                    })}
                    {hideDefaultCheckbox
                        ? 'Default Card*'
                        : this.renderCheckbox({
                            dataTest: { field: 'my-account-cc-default-card' },
                            isChecked: defaultcard,
                            value: 'defaultcard',
                            className: 'checkboxHD',
                            onChange: '',
                            label: 'Set As Default Card',
                        })}
                    <div className="margin-top-2x">
                        {this.renderButton({
                            label: submitButtonLabel,
                            dataTest: { field: 'my-account-cc-save-button' },
                            className: 'buttonHD',
                            onClick: null,
                            type: 'submit',
                        })}
                    </div>
                </form>
            </>
        );
    };

    desktopTemplate = (props, state) => {
        /* eslint-disable max-len */
        const {
            isReadyState,
            classes,
            cardtypes,
            handleSaveCreditCard,
        } = props;
        const {
            adornment: { cardnumber: iconType },
            data: { defaultcard, cardtype },
            hideDefaultCheckbox,
        } = state;
        let {
            modal: { title: modalTitle },
        } = props;
        let submitButtonLabel = '';
        if (modalTitle) {
            modalTitle = modalTitle.replace(/^(Edit|Add)/, '').trim();
        }
        if (modalTitle.indexOf('Add') !== -1) {
            submitButtonLabel = isReadyState
                ? ''.concat('Add ', modalTitle)
                : 'Adding...';
        } else {
            submitButtonLabel = isReadyState
                ? ''.concat('Save ', modalTitle)
                : 'Saving...';
        }

        const submitData = {
            handleSaveCreditCard,
        };

        return (
            <>
                <p>We Accept: Visa, Mastercard, American Express and Discover</p>
                <p>Desktop Modal</p>
                <form
                    onSubmit={(event) => this.handleSubmit(event, submitData)}
                    className={classes.form}
                >
                    <div className="display-none">
                        {this.renderSelect({
                            name: 'cardtype',
                            label: 'Card Type',
                            options: cardtypes,
                        })}
                    </div>
                    {this.renderInput({
                        name: 'cardholder',
                        dataTest: { field: 'my-account-cc-card-name' },
                        label: 'Name on Card',
                        type: 'text',
                        pattern: '^[a-zA-Z\\s\\.\\-&]*$',
                        length: 50,
                    })}
                    {this.renderInput({
                        name: 'cardnumber',
                        label: 'Card Number',
                        dataTest: { field: 'my-account-cc-card-number' },
                        type: 'tel',
                        pattern: '^[\\d]*$',
                        length: null,
                        adornment: iconType,
                        adornmentPosition: 'end',
                        mask: CreditCardMask,
                        onChangeHandler: this.handleCreditCardChange,
                        customClass: `-cardtype-${cardtype}-`,
                    })}
                    {this.renderInput({
                        name: 'expiration',
                        label: 'Expiration Date (MM/YY)',
                        dataTest: { field: 'my-account-cc-exp-date' },
                        type: 'tel',
                        pattern: '^[\\d\\s\\/]*$',
                        length: null,
                        mask: ExpirationDateMask,
                        onChangeHandler: this.handleExpirationDateChange,
                    })}
                    {hideDefaultCheckbox
                        ? 'Default Card*'
                        : this.renderCheckbox({
                            dataTest: { field: 'my-account-cc-default-card' },
                            isChecked: defaultcard,
                            value: 'defaultcard',
                            className: 'checkboxHD',
                            onChange: '',
                            label: 'Set As Default Card',
                        })}
                    <div className="margin-top-2x">
                        {this.renderButton({
                            label: submitButtonLabel,
                            dataTest: { field: 'my-account-cc-save-button' },
                            className: 'buttonHD',
                            onClick: null,
                            type: 'submit',
                        })}
                    </div>
                </form>
            </>
        );
    };

    render() {
        const { template } = this.props;
        if (template === 'desktop') {
            return this.desktopTemplate(this.props, this.state);
        }
        return this.mobileTemplate(this.props, this.state);
    }
}

CreditCardForm.defaultProps = {
    handleSaveCreditCard: () => { },
    template: '',
};

CreditCardForm.propTypes = {
    classes: object.isRequired,
    isReadyState: bool.isRequired,
    cardtypes: arrayOf(
        shape({
            id: string,
            name: string,
        }),
    ).isRequired,
    wallet: arrayOf(
        shape({
            walletId: string.isRequired,
            cardType: string.isRequired,
            payType: string.isRequired,
            cardHolderName: string.isRequired,
            cardNumber: string.isRequired,
            lastFourDigits: string.isRequired,
            expiryDate: string.isRequired,
            status: string,
            subsIndicator: string,
        }),
    ).isRequired,
    errorField: shape({
        message: string,
        type: string,
        name: string,
    }).isRequired,
    handleSaveCreditCard: func,
    template: string,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    return {
        isReadyState: newState.isReadyState,
        cardtypes: newState.cardtypes,
        modal: newState.modal,
        wallet: newState.wallet,
        errorField: newState.errorField || {},
        toggleState: newState.toggleState,
        template: newState.template,
    };
};

const mapDispatchToProps = (dispatch) => ({
    handleSaveCreditCard: (wallet) => dispatch(EventDispatcher.handleSaveCreditCard(wallet)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles)(CreditCardForm));
